'use client';

import type { Brand } from '@/types/constants';
import { GameUrl } from '../../../game/subcomponents/game-url/game-url';
import type { ReactElement } from 'react';
import { type PropsWithChildren } from 'react';
import { useStore } from '@/store/store';
import { RewardMachineWelcomeModalTrigger } from '../reward-machine-welcome-modal-trigger';
import Link from 'next/link';
import { ROUTES } from '@/utils/constants';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getRMGameLaunchedProps } from '@/tracking/amplitude/events/rewards/reward-machine-events';
import type { LauncherUrlProps } from '@/utils/url/launcher-url';

type RewardMachineGameUrlComponentProps = PropsWithChildren<{
    providerName: string;
    gameName: string;
    rgi: string;
    disableExitScreenRedirect?: boolean;
    freeSpins?: number;
    theme?: string;
    shouldTriggerModal?: boolean;
    onClick?: () => void;
    page?: string;
}>;
export const RewardMachineGameUrlComponent: React.FC<RewardMachineGameUrlComponentProps> = ({
    children,
    providerName,
    gameName,
    rgi,
    theme,
    onClick,
    page,
    freeSpins,
    shouldTriggerModal = true,
    disableExitScreenRedirect = false
}) => {
    const { userContext } = useStore(); // TODO: read from globals
    const { isLoggedIn } = userContext;
    const brand = process.env.BRAND as Brand;
    const { region } = useGlobalContext();

    const rewardMachineLauncherUrl: LauncherUrlProps = {
        providerName,
        region,
        brand,
        gameName,
        rgi,
        theme: theme || 'default',
        ...(page ? { page } : {})
    };

    if (!disableExitScreenRedirect && isLoggedIn && freeSpins === 0) {
        return <Link href={ROUTES.REWARD_MACHINE_EXIT}>{children}</Link>;
    }

    const gameUrlElement = (
        <GameUrl
            launcherUrlProps={rewardMachineLauncherUrl}
            onLauncherClick={onClick}
            amplitudeEventProps={getRMGameLaunchedProps(false)}>
            {children as ReactElement}
        </GameUrl>
    );

    const gameUrlFromModalElement = (
        <GameUrl
            launcherUrlProps={rewardMachineLauncherUrl}
            onLauncherClick={onClick}
            amplitudeEventProps={getRMGameLaunchedProps(true)}>
            {children as ReactElement}
        </GameUrl>
    );

    if (!shouldTriggerModal) {
        return <div onClick={onClick}>{gameUrlElement}</div>;
    }

    if (!isLoggedIn) {
        return gameUrlElement;
    }

    return <RewardMachineWelcomeModalTrigger>{gameUrlFromModalElement}</RewardMachineWelcomeModalTrigger>;
};
